import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Trophy, Medal, ArrowLeft } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Leaderboard({ agent }) {
  const navigate = useNavigate();
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/leaderboard`);
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getRankColor = (rank) => {
    if (rank === 1) return 'text-[#FFD700]';
    if (rank === 2) return 'text-[#C0C0C0]';
    if (rank === 3) return 'text-[#CD7F32]';
    return 'text-slate-400';
  };

  return (
    <div className="min-h-screen cosmic-bg">
      {/* Header */}
      <header className="border-b border-white/10 backdrop-blur-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Button
              data-testid="back-btn"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="mr-2" size={18} />
              Back
            </Button>
            <h1 className="font-unbounded text-2xl font-bold text-gradient">LEADERBOARD</h1>
            <div className="w-24"></div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-center"
        >
          <Trophy size={48} className="mx-auto mb-4 text-[#FFD700]" />
          <h2 className="font-unbounded text-3xl md:text-4xl font-bold mb-2">Global Rankings</h2>
          <p className="font-inter text-slate-400">The most powerful Manaverses in the arena</p>
        </motion.div>

        <Card data-testid="leaderboard-table" className="glass-card border-white/10 max-w-6xl mx-auto">
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="font-rajdhani text-xl text-slate-400">Loading leaderboard...</p>
              </div>
            ) : leaderboard.length === 0 ? (
              <div className="text-center py-12">
                <p className="font-rajdhani text-xl text-slate-400">No agents yet. Be the first!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="font-rajdhani text-sm text-slate-400 text-left py-3 px-4">RANK</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-left py-3 px-4">AGENT</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-left py-3 px-4">MANAVERSE</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-left py-3 px-4">TYPE</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-left py-3 px-4">STAGE</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-right py-3 px-4">W/L</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-right py-3 px-4">WIN %</th>
                      <th className="font-rajdhani text-sm text-slate-400 text-right py-3 px-4">ELO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaderboard.map((entry) => (
                      <motion.tr
                        key={entry.rank}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: entry.rank * 0.02 }}
                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                        data-testid={`leaderboard-row-${entry.rank}`}
                      >
                        <td className="py-4 px-4">
                          <span className={`font-unbounded text-lg font-bold ${getRankColor(entry.rank)}`}>
                            {getRankIcon(entry.rank)}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-rajdhani text-white font-semibold">{entry.agent_name}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-rajdhani text-[#00F0FF]">{entry.manaverse_name}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-inter text-sm text-slate-400">{entry.type}</span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="font-rajdhani text-sm px-2 py-1 bg-[#7000FF]/20 border border-[#7000FF]/50 rounded-full text-white">
                            {entry.stage}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-mono text-sm text-slate-300">
                            {entry.wins}W - {entry.losses}L
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-mono text-sm font-bold text-[#00FF9D]">{entry.win_rate}%</span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <span className="font-mono text-lg font-bold text-white">{entry.elo}</span>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}